/** Generated (transcribed) invoice — "Send as" choice, side-by-side preview and PDF download. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileOutput, Loader2, RefreshCw, Scissors } from 'lucide-react';
import { Button } from '../ui/button';

export const COMPANY_LABEL = { DEV: 'LUX Developments', CON: 'LUX Construction' };

export async function downloadGeneratedPdf(api, itemId, payload, filename) {
  const r = await api.post(`/cost-inbox/${itemId}/generated-pdf`, payload || {}, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a'); a.href = url; a.download = filename || 'generated-invoice.pdf'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

const IMG_RX = /\.(jpe?g|png|webp|heic|heif)$/i;
export function emailSourceKind(item) {
  const em = item?.email_in;
  if (!em) return null;
  if (em.body_used) return 'email text';
  const names = em.attachments || [];
  if (names.some(n => /\.docx?$/i.test(n))) return 'Word document';
  if (names.length && names.every(n => IMG_RX.test(n))) return 'photo';
  return null;
}

export function SendAsBlock({ value, onChange, readOnly, item }) {
  const forced = !!item?.split;
  const generated = value === 'generated';
  const kind = emailSourceKind(item);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid="inbox-send-as-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs text-gray-300 font-medium inline-flex items-center gap-1.5"><FileOutput className="w-3.5 h-3.5 text-[#F5A623]" /> Send to Xero / LiveCosts as</div>
          <div className="text-[11px] text-gray-500">{generated
            ? 'A clean invoice built from the figures you enter here — for email text, photos and Word documents. The original stays on this record.'
            : 'The document exactly as it was received.'}</div>
        </div>
        {readOnly ? (
          <span className="text-sm font-semibold text-white px-2.5 py-1 rounded bg-white/5 border border-white/10" data-testid="inbox-send-as-readonly">{generated ? 'Generated invoice' : 'Original document'}</span>
        ) : (
          <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid="inbox-send-as-toggle">
            {[{ k: 'original', l: 'Original document' }, { k: 'generated', l: 'Generated invoice' }].map(o => (
              <button key={o.k} type="button" disabled={forced} onClick={() => onChange(o.k)}
                className={`px-3 h-8 text-xs font-medium transition-colors disabled:cursor-not-allowed ${value === o.k ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-[#1E2530] text-gray-300 hover:bg-white/5'}`}
                data-testid={`inbox-send-as-${o.k}`}>{o.l}</button>
            ))}
          </div>
        )}
      </div>
      {forced && <div className="mt-2 text-[11px] text-sky-200/90" data-testid="inbox-send-as-forced">Split copies always go out as a generated invoice — the original shows the supplier's full total.</div>}
      {!forced && !generated && !readOnly && kind && (
        <div className="mt-2 text-[11px] text-[#F5A623]/90" data-testid="inbox-send-as-hint">
          This came in as {kind === 'email text' ? 'email text' : `a ${kind}`} — switch to <b>Generated invoice</b> to send Xero / LiveCosts a clean invoice built from the figures you enter.
        </div>
      )}
    </div>
  );
}

export function GeneratedPreview({ api, itemId, getPayload, live, filename }) {
  const [state, setState] = useState({ loading: true, pages: [], count: 0, error: null });
  const [downloading, setDownloading] = useState(false);
  const payloadRef = useRef(getPayload);
  payloadRef.current = getPayload;
  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.post(`/cost-inbox/${itemId}/generated-preview`, live ? payloadRef.current() : {});
      setState({ loading: false, pages: r.data.pages || [], count: r.data.page_count || 0, error: null });
    } catch (e) {
      setState({ loading: false, pages: [], count: 0, error: e.response?.data?.detail || 'Could not build the preview' });
    }
  }, [api, itemId, live]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    setDownloading(true);
    try { await downloadGeneratedPdf(api, itemId, live ? payloadRef.current() : {}, filename); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not download the PDF'); }
    finally { setDownloading(false); }
  };
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="generated-preview">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-white/10 bg-[#1E2530] text-xs text-gray-300 shrink-0">
        <span className="inline-flex items-center gap-1.5 text-[#F5A623]"><FileOutput className="w-3.5 h-3.5" /> Generated invoice{state.count > 1 ? ` · ${state.count} pages` : ''}</span>
        <span className="text-gray-500 hidden sm:inline">— this is what Xero / LiveCosts will receive</span>
        <span className="flex-1" />
        <button type="button" onClick={load} disabled={state.loading} className="px-2 py-0.5 rounded hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-50" data-testid="generated-preview-refresh">
          {state.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
        </button>
        <button type="button" onClick={download} disabled={downloading || !!state.error} className="px-2 py-0.5 rounded hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-50" data-testid="generated-preview-download">
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto bg-[#0F141B] p-3">
        {state.error ? (
          <div className="h-full flex items-center justify-center text-sm text-amber-300 text-center px-6" data-testid="generated-preview-error">{state.error}</div>
        ) : state.loading && !state.pages.length ? (
          <div className="h-full flex items-center justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            {state.pages.map((b64, i) => (
              <img key={i} src={`data:image/png;base64,${b64}`} alt={`Generated invoice page ${i + 1}`} className={`block w-full max-w-[900px] bg-white shadow-2xl ${state.loading ? 'opacity-60' : ''}`} data-testid={`generated-preview-page-${i}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function DownloadGeneratedButton({ api, item }) {
  const [busy, setBusy] = useState(false);
  if (!item || item.send_as !== 'generated' || item.status !== 'posted') return null;
  const run = async () => {
    setBusy(true);
    try { await downloadGeneratedPdf(api, item.id, {}, `${item.supplier_name || 'invoice'} ${item.reference || ''} - generated.pdf`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not download the PDF'); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={run} disabled={busy} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="inbox-download-generated-btn">
      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileOutput className="w-4 h-4 mr-1.5" />} Generated PDF
    </Button>
  );
}

export function DocTabs({ tab, setTab }) {
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-[#161c25] shrink-0" data-testid="inbox-doc-tabs">
      {[{ k: 'original', l: 'Original' }, { k: 'generated', l: 'Generated invoice', icon: FileOutput }].map(o => (
        <button key={o.k} type="button" onClick={() => setTab(o.k)}
          className={`px-3 h-7 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${tab === o.k ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-300 hover:bg-white/10'}`}
          data-testid={`inbox-doc-tab-${o.k}`}>
          {o.icon ? <o.icon className="w-3.5 h-3.5" /> : null}{o.l}
        </button>
      ))}
      <span className="ml-auto text-[11px] text-gray-500 hidden sm:inline-flex items-center gap-1"><Scissors className="w-3 h-3" /> Original kept on record · generated copy is what gets sent</span>
    </div>
  );
}
